/**
 * i18n setup — react-i18next
 *
 * Vietnamese is the default. English is opt-in via the topbar LanguageToggle.
 *
 * Usage:
 *   import { useTranslation } from 'react-i18next';
 *
 *   // Common keys only:
 *   const { t } = useTranslation('common');
 *   <button>{t('action.create')}</button>
 *
 *   // Feature namespace + fallback to common:
 *   const { t } = useTranslation(['finance', 'common']);
 *   <span>{t('finance:kpi.totalRevenue')}</span>
 *   <button>{t('common:action.save')}</button>
 *
 * Change language:
 *   i18n.changeLanguage('en');     // persists to localStorage 'ams.lang'
 *
 * Per-feature namespaces (manager portal):
 *   common, auth, finance, properties, contracts, calendar, tickets,
 *   renters, vehicles, violations, settings, dashboard
 *
 * Add new keys directly to the locale JSON files under locales/{vi,en}/.
 * Keep VN and EN files structurally 1:1 — same key paths in both.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import commonVi from './locales/vi/common.json';
import commonEn from './locales/en/common.json';
import authVi from './locales/vi/auth.json';
import authEn from './locales/en/auth.json';
import financeVi from './locales/vi/finance.json';
import financeEn from './locales/en/finance.json';
import propertiesVi from './locales/vi/properties.json';
import propertiesEn from './locales/en/properties.json';
import contractsVi from './locales/vi/contracts.json';
import contractsEn from './locales/en/contracts.json';
import calendarVi from './locales/vi/calendar.json';
import calendarEn from './locales/en/calendar.json';
import ticketsVi from './locales/vi/tickets.json';
import ticketsEn from './locales/en/tickets.json';
import rentersVi from './locales/vi/renters.json';
import rentersEn from './locales/en/renters.json';
import vehiclesVi from './locales/vi/vehicles.json';
import vehiclesEn from './locales/en/vehicles.json';
import violationsVi from './locales/vi/violations.json';
import violationsEn from './locales/en/violations.json';
import settingsVi from './locales/vi/settings.json';
import settingsEn from './locales/en/settings.json';
import dashboardVi from './locales/vi/dashboard.json';
import dashboardEn from './locales/en/dashboard.json';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'vi',
    supportedLngs: ['vi', 'en'],
    defaultNS: 'common',
    ns: [
      'common', 'auth', 'finance', 'properties', 'contracts', 'calendar',
      'tickets', 'renters', 'vehicles', 'violations', 'settings', 'dashboard',
    ],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'ams.lang',
      caches: ['localStorage'],
    },
    resources: {
      vi: {
        common: commonVi,
        auth: authVi,
        finance: financeVi,
        properties: propertiesVi,
        contracts: contractsVi,
        calendar: calendarVi,
        tickets: ticketsVi,
        renters: rentersVi,
        vehicles: vehiclesVi,
        violations: violationsVi,
        settings: settingsVi,
        dashboard: dashboardVi,
      },
      en: {
        common: commonEn,
        auth: authEn,
        finance: financeEn,
        properties: propertiesEn,
        contracts: contractsEn,
        calendar: calendarEn,
        tickets: ticketsEn,
        renters: rentersEn,
        vehicles: vehiclesEn,
        violations: violationsEn,
        settings: settingsEn,
        dashboard: dashboardEn,
      },
    },
  });

export default i18n;
